import { Navigation } from "components/organisms/Navigation";
import { AppSidebar } from "components/organisms/AppSidebar";
import styles from "./LayoutDashboard.module.scss";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { Button } from "components/atoms/Button";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "components/atoms/Modal";
import { ReactNode, useState } from "react";
import { useMedia } from "react-use";
import {
  getLocalStorageItem,
  setLocalStorageItem,
  LocalStorageKeys,
} from "utils/react/localStorage";

export const LayoutDashboard = ({ children }: { children?: ReactNode }) => {
  const router = useRouter();
  const [isDowntimeModalOpen, setIsDowntimeModalOpen] = useState(false);
  // Phone-sized viewports have no sidebar at all: navigation lives in the
  // header's menu drawer instead. Defaults to false so SSR renders the
  // desktop layout.
  const isMobileViewport = useMedia("(max-width: 767px)", false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return (
      getLocalStorageItem(LocalStorageKeys.CONNEXX_SIDEBAR_COLLAPSED) === "true"
    );
  });

  const handleSidebarToggle = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      setLocalStorageItem(LocalStorageKeys.CONNEXX_SIDEBAR_COLLAPSED, String(next));
      return next;
    });
  };

  return (
    <div className={styles.LayoutDashboard}>
      {!isMobileViewport && (
        <AppSidebar collapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />
      )}
      <div className={styles.LayoutDashboard__Right}>
        <Navigation />
        <motion.main
          key={router.route}
          initial="hidden"
          animate="enter"
          exit="exit"
          className={styles.LayoutDashboard__Main}
          transition={{
            ease: "easeInOut",
            duration: 1,
          }}
        >
          {children}
        </motion.main>
      </div>

      <Modal
        isOpen={isDowntimeModalOpen}
        handleClose={() => setIsDowntimeModalOpen(false)}
        size="md"
      >
        <ModalHeader>Scheduled maintenance</ModalHeader>
        <ModalBody>
          <p className="mb-4">
            Connexx will be unavailable from 8:00pm GMT on Wednesday, 24
            December due to essential system maintenance.
          </p>
          <p className="mb-6">
            We expect the maintenance window to last 5 hours but may last up to
            24 hours. We will restore service as soon as the work is completed.
          </p>

          <div className="font-semibold mb-2">
            Why is this maintenance taking place?
          </div>
          <p className="mb-6">
            This maintenance is required to carry out essential system upgrades
            and housekeeping to ensure the ongoing stability, security, and
            performance of Connexx.
          </p>

          <div className="font-semibold mb-2">
            Will I be able to create or download shipping labels during the
            maintenance?
          </div>
          <p className="mb-4">
            No. During the maintenance window, Connexx will be unavailable and
            shipping labels cannot be created or downloaded.
          </p>

          <p className="mb-4">
            We recommend completing any time-critical shipping activity before
            the maintenance begins.
          </p>

          <p className="mb-4">
            If you have any questions, please contact support@connexx.co.uk.
          </p>

          <p>Thank you.</p>
        </ModalBody>
        <ModalFooter>
          <div className="ml-auto">
            <Button
              colorScheme="primary"
              onClick={() => setIsDowntimeModalOpen(false)}
            >
              Close
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
};
